import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';

import { CriteriumIconAndText, CriteriumSkeleton } from '@hydra/common/criterium';
import { Icon, Label, Image } from '@hydra/common/components';
import { useReviewContext } from '@hydra/common/context';
import StructuredDataIssueText from '@hydra/common/components/StructuredDataIssueText';

const useStyles = makeStyles(theme => ({
  previewVertical: {
    maxWidth: '500px',
    display: 'flex',
    padding: theme.spacing(1.5, 0)
  },
  previewHorizontal: {
    maxWidth: '500px',
    display: 'block',
    padding: theme.spacing(1.5, 0)
  },
  imageIssue: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '25px 0 0 25px'
  },
  imageVertical: {
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '25px 0 0 25px'
  },
  imageHorizontal: {
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '25px 25px 0 0'
  },
  imageContainerVerical: {
    height: '135px',
    width: '135px'
  },
  imageContainerHorizontal: {
    height: '230px',
    width: '100%'
  },
  contentVertical: {
    padding: theme.spacing(2),
    flex: 1,
    width: '100%',
    height: '135px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '0 25px 25px 0',
    borderLeft: 'none'
  },
  contentHorizontal: {
    padding: theme.spacing(2),
    flex: 1,
    width: '100%',
    height: '120px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '0 0 25px 25px',
    borderTop: 'none'
  },
  title: {
    display: 'grid',
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.fontSizeBase,
    fontWeight: theme.typography.fontWeightRegular
  },
  description: {
    display: 'grid',
    marginBottom: theme.spacing(1)
  }
}));

const getTranslatedIssue = (issue) => {
  return (
    <CriteriumIconAndText
      success={false}
      text={<StructuredDataIssueText issue={issue} />}
    />
  );
};

export const CriteriumTwitterCardPreview = ({ properties, errors, storedImage, url }) => {
  const { isPdf, variant } = useReviewContext();
  const classes = useStyles();
  const imageIssue = errors.find(({ meta }) => meta.property === 'twitter:image');
  const titleIssue = errors.find(({ meta }) => meta.property === 'twitter:title');
  const descriptionIssue = errors.find(({ meta }) => meta.property === 'twitter:description');

  const propertyCard = properties.find(({ name }) => name === 'twitter:card') || {};
  const propertyImage = properties.find(({ name }) => name === 'twitter:image') || {};
  const imageUrl = storedImage ? storedImage.url : propertyImage.content;

  let orientation = 'vertical';
  if (!imageIssue && propertyCard.content === 'summary_large_image') {
    orientation = 'horizontal';
  }

  return (
    <div className={orientation === 'vertical' ? classes.previewVertical : classes.previewHorizontal}>
      <div className={orientation === 'vertical' ? classes.imageContainerVerical : classes.imageContainerHorizontal}>
        {
          variant === 'nocrit'
            ? <CriteriumSkeleton style={{ height: '100%', transform: 'scale(1, 1)', borderRadius: '25px', borderTopRightRadius: '0', borderBottomRightRadius: '0' }} />
            : (
                imageIssue
                  ? (
                    <div className={classes.imageIssue}>
                      <Icon type='woorank-danger' color='error' size='xs' />
                    </div>
                    )
                  : (
                    <Image
                      src={imageUrl}
                      className={orientation === 'vertical' ? classes.imageVertical : classes.imageHorizontal}
                      width='100%'
                      height='100%'
                      background
                      defer={!isPdf}
                    />
                    )
              )
        }
      </div>
      <div className={orientation === 'vertical' ? classes.contentVertical : classes.contentHorizontal}>
        <div className={classes.title}>
          <Label truncate noBackground>
            {
              variant === 'nocrit'
                ? <CriteriumSkeleton width='60%' />
                : (titleIssue ? getTranslatedIssue(titleIssue) : properties.find(({ name }) => name === 'twitter:title').content)
            }
          </Label>
        </div>
        <div className={classes.description}>
          <Label truncate noBackground>
            {
              variant === 'nocrit'
                ? <CriteriumSkeleton width='60%' />
                : descriptionIssue ? getTranslatedIssue(descriptionIssue) : properties.find(({ name }) => name === 'twitter:description').content
            }
          </Label>
        </div>
        {variant === 'nocrit' ? <CriteriumSkeleton width='30%' /> : (url ? url.split('/')[0] : url)}
      </div>
    </div>
  );
};

CriteriumTwitterCardPreview.propTypes = {
  properties: PropTypes.array,
  issues: PropTypes.array,
  storedImage: PropTypes.object,
  url: PropTypes.string
};

export default CriteriumTwitterCardPreview;
